import React, { useEffect, useCallback, useMemo, useContext, useState } from 'react';

import PropTypes from 'prop-types';

import { CustomsPropType } from '../../../prop-types/customs';
import { emptyCustomsItem } from '../../../services/customs-service';
import * as Actions from './actions';
import customsReducer from './reducer';
import { useTaxIdentifiers } from '../../../hooks/use-tax-identifier';
import { getCustomsItemsCurrencyCode } from '../../../fulfillment-planner/helpers/calculate-customs';
import { useSelector } from 'react-redux';
import Global from '../../../store/global-selectors';
import { useCustomsStoreCurrency } from '../../../hooks/use-seller-setting';

const CustomsContext = React.createContext();

const useCustomsContext = () => {
  const context = useContext(CustomsContext);
  if (!context) {
    throw new Error('useCustomsContext should be used within CustomsProvider');
  }
  return context;
};

/**
 * @typedef {import('../types').CustomsDeclaration} CustomsDeclaration
 */
/**
 * @param {object} props
 * @param {CustomsDeclaration} props.customs - The current customs state
 * @param {(customs: CustomsDeclaration)=> void} props.saveCustoms - Function to save the updated customs state
 * @param {string} props.defaultCountryCode - Default country code for new customs items
 * @param {string} props.defaultCurrencyCode - Default currency code for new customs items
 * @param {string} props.defaultWeightUnit - Default weight unit for new customs items
 * @param {string|null} [props.fulfillmentPlanId] - Optional fulfillment plan ID to scope tax identifiers
 * @param {React.ReactNode} props.children - Child components that will consume the customs context
 * @returns {JSX.Element}
 */
const CustomsProvider = ({
  customs,
  saveCustoms,
  defaultCountryCode,
  defaultCurrencyCode,
  defaultWeightUnit,
  fulfillmentPlanId = null,
  children,
}) => {
  const [customsState, setCustomsState] = useState(customs);

  useEffect(() => {
    setCustomsState(customs);
  }, [customs]);

  const { taxIdentifiers } = useTaxIdentifiers({ includeInactive: true });
  const isCustomsStoreCurrencySettingEnabled = useCustomsStoreCurrency();
  const defaultCustomsCurrencyCode = getCustomsItemsCurrencyCode(customsState, defaultCurrencyCode, isCustomsStoreCurrencySettingEnabled);

  const aggregateFulfillmentTaxIdentifiers = useSelector((state) => {
    const fulfillments = Global.selectEntitiesFulfillmentsByFulfillmentPlanId(state, { fulfillmentPlanId });
    return fulfillments.flatMap((f) => (f?.sellerTaxIdentifiers ? f.sellerTaxIdentifiers : []));
  });

  const customsTaxIdentifiers = useMemo(() => {
    return taxIdentifiers.filter((taxId) => aggregateFulfillmentTaxIdentifiers?.includes(taxId.taxIdentifierId));
  }, [aggregateFulfillmentTaxIdentifiers, taxIdentifiers]);

  const dispatch = useCallback(
    (action) => {
      const updatedCustoms = customsReducer(customsState, action);
      if (customsState !== updatedCustoms) {
        setCustomsState(updatedCustoms);
        saveCustoms(updatedCustoms);
      }
    },
    [customsState, setCustomsState, saveCustoms],
  );

  const generateDeclaration = useCallback(() => {
    return emptyCustomsItem({
      defaultCountryCode,
      currencyCode: defaultCustomsCurrencyCode,
      weightUnit: defaultWeightUnit,
    });
  }, [defaultCountryCode, defaultCustomsCurrencyCode, defaultWeightUnit]);

  const addDeclaration = useCallback(() => {
    const action = Actions.addDeclaration(generateDeclaration());
    dispatch(action);
  }, [dispatch, generateDeclaration]);

  const removeDeclaration = useCallback(
    (declarationKey) => {
      const action = Actions.removeDeclaration(declarationKey, generateDeclaration);
      dispatch(action);
    },
    [dispatch, generateDeclaration],
  );

  const updateDeclaration = useCallback(
    (declarationKey, updatedPayload) => {
      const action = Actions.updateDeclaration(declarationKey, updatedPayload);
      dispatch(action);
    },
    [dispatch],
  );

  const updateContentType = useCallback(
    (contentType) => {
      const action = Actions.updateContentType(contentType);
      dispatch(action);
    },
    [dispatch],
  );

  const updateDutiesPaid = useCallback(
    (dutiesPaid) => {
      const action = Actions.updateDutiesPaid(dutiesPaid);
      dispatch(action);
    },
    [dispatch],
  );

  const updatePostagePaid = useCallback(
    (postagePaid) => {
      const action = Actions.updatePostagePaid(postagePaid);
      dispatch(action);
    },
    [dispatch],
  );

  const updateNonDelivery = useCallback(
    (nonDelivery) => {
      const action = Actions.updateNonDelivery(nonDelivery);
      dispatch(action);
    },
    [dispatch],
  );

  const updateItn = useCallback(
    (exemptionCodeOrITN) => {
      const action = Actions.updateItnOrExemption(exemptionCodeOrITN);
      dispatch(action);
    },
    [dispatch],
  );

  const updateExportDeclarationNumber = useCallback(
    (exportDeclarationNumber) => {
      dispatch(Actions.updateExportDeclarationNumber(exportDeclarationNumber));
    },
    [dispatch],
  );

  const updateInvoiceNumber = useCallback(
    (invoiceNumber) => {
      dispatch(Actions.updateInvoiceNumber(invoiceNumber));
    },
    [dispatch],
  );

  const updateImporterOfRecord = useCallback(
    (importerOfRecord) => {
      dispatch(Actions.updateImporterOfRecord(importerOfRecord));
    },
    [dispatch],
  );

  const customsContextValue = useMemo(
    () => ({
      ...customsState,
      fulfillmentPlanId,
      addDeclaration,
      defaultCurrencyCode,
      removeDeclaration,
      updateDeclaration,
      updateContentType,
      updateDutiesPaid,
      updatePostagePaid,
      updateNonDelivery,
      updateItn,
      customsTaxIdentifiers,
      updateExportDeclarationNumber,
      updateInvoiceNumber,
      updateImporterOfRecord,
    }),
    [
      customsState,
      fulfillmentPlanId,
      addDeclaration,
      defaultCurrencyCode,
      removeDeclaration,
      updateDeclaration,
      updateContentType,
      updateDutiesPaid,
      updatePostagePaid,
      updateNonDelivery,
      updateItn,
      customsTaxIdentifiers,
      updateExportDeclarationNumber,
      updateInvoiceNumber,
      updateImporterOfRecord,
    ],
  );

  return <CustomsContext.Provider value={customsContextValue}>{children}</CustomsContext.Provider>;
};

CustomsProvider.propTypes = {
  customs: CustomsPropType.isRequired,
  saveCustoms: PropTypes.func.isRequired,
  defaultCountryCode: PropTypes.string.isRequired,
  defaultCurrencyCode: PropTypes.string.isRequired,
  defaultWeightUnit: PropTypes.string.isRequired,
  fulfillmentPlanId: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

export { CustomsProvider, CustomsContext, useCustomsContext };
